// AuthTokenHandoffFlow — one-time OAuth/invite handoff exchange (ledger A15, contract 06 §8).
// The opaque URL value is exchanged once, stale auth is replaced, and an optional server-owned
// account selection is delegated through module 07's public API. URL stripping/navigation stay
// with the shell query-callback seam.
import { AuthError } from '../models/auth.error'
import type { TokenHandoffExchangeResponse } from '../models/auth.types'
import { authService, type AuthService } from '../service/AuthService'
import { useAuthStore, type AuthStore } from '../stores/authStore'
import { runSessionResets } from '../stores/sessionReset'

import { SelectAccountFlow } from '@/features/account'

interface AccountSelector {
  execute(account: { id: number }): Promise<unknown>
}

export interface AuthTokenHandoffDeps {
  service?: AuthService
  store?: AuthStore
  accountSelector?: AccountSelector
  /** Clears every feature owned by the bearer being replaced. */
  resetSession?: () => void
}

export class AuthTokenHandoffFlow {
  private readonly service: AuthService
  private readonly store: AuthStore
  private readonly accountSelector: AccountSelector | undefined
  private readonly resetSession: () => void

  constructor(deps: AuthTokenHandoffDeps = {}) {
    this.service = deps.service ?? authService
    this.store = deps.store ?? useAuthStore()
    this.accountSelector = deps.accountSelector
    this.resetSession = deps.resetSession ?? runSessionResets
  }

  async execute(handoff: string): Promise<TokenHandoffExchangeResponse> {
    if (!handoff.trim()) throw new AuthError('handoffInvalid', 'auth.login.systemError')

    try {
      const result = await this.service.exchangeTokenHandoff(handoff)
      this.resetSession()
      this.store.reset()
      this.store.setToken(result.token)

      const accountId = Number(result.account)
      if (result.account !== undefined && Number.isFinite(accountId) && accountId > 0) {
        await (this.accountSelector ?? new SelectAccountFlow()).execute({ id: accountId })
      }
      return result
    } catch (error) {
      throw AuthError.from(error)
    }
  }
}
